import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {getEventInfo} from 'src/modules/event/services';
import {EventCreationMode, PushNotificationIds} from 'src/modules/event-create/constants';
import {Params} from 'src/modules/event-create/event-create.controller';
import {
    useEventTriggerAttachment,
} from 'src/modules/event-create/header-block/event-trigger-attachment/hooks/useEventTriggerAttachment';
import {setTriggerAttachments} from 'src/modules/event-create/header-block/event-trigger-attachment/services/actions';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {uploadTriggerAttachment} from 'src/openapi/apis/TriggerAttachmentControllerApi';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch, useAppSelector} from 'src/store';
import {getTriggerAttachmentType} from 'src/utils/getTriggerAttachmentType';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {EventTriggerAttachmentView} from './event-trigger-attachment.view';

type Props = {
    mode: EventCreationMode,
    eventChangeRequest: EventChangeRequest,
    validationHighLight: boolean,
};

const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventTriggerAttachmentController = (props: Props) => {
    const {
        mode,
        eventChangeRequest,
        validationHighLight,
    } = props;

    const dispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const eventId = parseInt(useParams<Params>().eventId || '');
    const changeRequestUpdateId = parseInt(useParams<Params>().changeRequestId || '');
    const changeRequestInfo = useAppSelector(getChangeRequestInfo)?.[changeRequestUpdateId];
    const eventCodeInfo = useAppSelector(getEventInfo)?.[eventId];
    const {addAttachment} = useEventTriggerAttachment();
    const [isUploading, setIsUploading] = useState<boolean>(false);

    useEffect(() => {
        if (changeRequestUpdateId && changeRequestInfo) {
            dispatch(setTriggerAttachments(changeRequestInfo.triggerAttachments));
            return;
        }

        if (eventId && eventCodeInfo) {
            dispatch(setTriggerAttachments(eventCodeInfo.triggerAttachments));
        }
    }, [changeRequestUpdateId, changeRequestInfo, eventId, eventCodeInfo, dispatch]);

    const onUploadFiles = async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0 || isUploading) {
            return;
        }

        const file = acceptedFiles[0];

        setIsUploading(true);

        const response = await dispatch(uploadTriggerAttachment({file}));

        setIsUploading(false);

        if (response.payload.success) {
            const attachment = response.payload.data;
            const attachmentName = attachment.attachmentName;
            addAttachment(attachmentName, getTriggerAttachmentType(file.type));
        }

        if (response.payload.error) {
            await onCreateNote({
                id: PushNotificationIds.uploadTriggerAttachmentFailure,
                status: Status.ERROR,
                message: 'Failed to upload attachment. Please try again.',
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    return (
        <EventTriggerAttachmentView
            mode={mode}
            eventChangeRequest={eventChangeRequest}
            validationHighLight={validationHighLight}
            isUploading={isUploading}
            onUploadFiles={onUploadFiles}
        />
    );
};
